/**
 * P2-03 — the parent's code screen: "Resend code in 0:42", and the number kept
 * across a reload.
 *
 * 🔴 THE COOLDOWN IS THE SERVER'S `RESEND_COOLDOWN_MS` (backend
 * `modules/auth/otp.ts`: 60 000), COPIED RATHER THAN GUESSED. A button that
 * offered a resend the server then refused would spend one of the five codes an
 * hour a number is allowed on a request that cannot succeed — and say nothing
 * the parent could act on.
 */
export const RESEND_COOLDOWN_SECONDS = 60

/** Whole seconds until a new code may be asked for; 0 once it may. */
export function resendSecondsLeft(
  sentAtMs: number | null,
  nowMs: number,
  cooldownSeconds: number = RESEND_COOLDOWN_SECONDS,
): number {
  if (sentAtMs === null || !Number.isFinite(sentAtMs)) return 0
  const left = Math.ceil((sentAtMs + cooldownSeconds * 1000 - nowMs) / 1000)
  // A clock that moved backwards must not produce a wait longer than the rule.
  return Math.min(Math.max(left, 0), cooldownSeconds)
}

/** `42` → "0:42", `60` → "1:00" — Figma M2's countdown. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`
}

/* ─────────────────────────────── the number, across a reload (this tab) ── */

/**
 * Minimal `sessionStorage` shape, injectable so the tests need no DOM — the
 * same shim every store in `shared/stores` uses.
 */
export interface OtpHopStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface OtpHop {
  /** E.164, `+91` and ten digits — the only shape the phone screen produces. */
  phone: string
  /** When the code was last asked for, for the countdown. */
  sentAt: number
}

export const OTP_HOP_KEY = 'kalqy.parent.otpHop'

/**
 * ⚠️ HOW LONG A REMEMBERED NUMBER IS WORTH KEEPING: the code's own life (five
 * minutes, `OTP_TTL_MS`) and a little more. Past that the code is dead whatever
 * the screen says, so the number is dropped and the parent starts again rather
 * than typing digits into a screen that cannot check them.
 */
export const OTP_HOP_MAX_AGE_MS = 10 * 60_000

const E164_IN = /^\+91\d{10}$/

function defaultTabStorage(): OtpHopStorage | null {
  try {
    return (globalThis as { sessionStorage?: OtpHopStorage }).sessionStorage ?? null
  } catch {
    return null
  }
}

/**
 * 🔴 `sessionStorage`, NEVER THE HASH AND NEVER `localStorage`. AppRouter keeps
 * this number out of the URL on purpose — a phone number in a URL is in browser
 * history, bookmarks and whatever a shared laptop syncs. This tab's own store
 * dies with the tab, which is the same lifetime the number had in memory, plus
 * the one thing that was missing: surviving a reload of the code screen.
 */
export function saveOtpHop(hop: OtpHop, storage: OtpHopStorage | null = defaultTabStorage()): void {
  if (!E164_IN.test(hop.phone) || !Number.isFinite(hop.sentAt)) return
  try {
    storage?.setItem(OTP_HOP_KEY, JSON.stringify(hop))
  } catch {
    /* A browser that refuses storage just loses the number on reload, as before. */
  }
}

export function loadOtpHop(
  nowMs: number,
  storage: OtpHopStorage | null = defaultTabStorage(),
): OtpHop | null {
  let raw: string | null
  try {
    raw = storage?.getItem(OTP_HOP_KEY) ?? null
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { phone, sentAt } = parsed as Partial<OtpHop>
    if (typeof phone !== 'string' || !E164_IN.test(phone)) return null
    if (typeof sentAt !== 'number' || !Number.isFinite(sentAt)) return null
    if (nowMs - sentAt > OTP_HOP_MAX_AGE_MS || sentAt - nowMs > 60_000) return null
    return { phone, sentAt }
  } catch {
    return null
  }
}

export function clearOtpHop(storage: OtpHopStorage | null = defaultTabStorage()): void {
  try {
    storage?.removeItem(OTP_HOP_KEY)
  } catch {
    /* Nothing to do: an unreadable store holds nothing we can clear. */
  }
}
